import { useState } from 'react'
import { useNavigate } from 'react-router'
import { AlertTriangle, Plus, Zap } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { DataTable } from '@/admin/components/data-table/data-table'
import type { DataTableColumn } from '@/admin/components/data-table/data-table'

import { useEmailTriggerList } from './use-email-triggers'
import type { TriggerKind, TriggerListRow } from './types'

const KIND_FILTERS: Array<{ value: TriggerKind | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'workflow_event', label: 'Workflow events' },
  { value: 'workflow_action', label: 'Workflow actions' },
  { value: 'api', label: 'API calls' },
]

const KIND_LABEL: Record<TriggerKind, string> = {
  workflow_event: 'Workflow event',
  workflow_action: 'Workflow action',
  api: 'API call',
}

const SEND_MODE_LABEL: Record<string, string> = {
  auto: 'Send automatically',
  review_first: 'Review first',
}

export function EmailTriggersTab() {
  const navigate = useNavigate()
  const [kind, setKind] = useState<TriggerKind | 'all'>('all')
  const { data: triggers = [], isLoading, error } = useEmailTriggerList(kind)

  const columns: DataTableColumn<TriggerListRow>[] = [
    {
      id: 'trigger_label',
      header: 'When this happens',
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-slate-900">
              {row.trigger_label || row.trigger_key}
            </span>
            {!row.key_is_resolvable && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 border border-amber-200"
                title="This key is no longer in the workflow or API allowlist."
              >
                <AlertTriangle size={10} />
                stale
              </span>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            {KIND_LABEL[row.trigger_kind]}
          </span>
        </div>
      ),
    },
    {
      id: 'condition',
      header: 'If',
      cell: (row) => (
        <span
          className={
            row.condition_summary
              ? 'text-xs text-slate-700'
              : 'text-xs text-slate-400'
          }
        >
          {row.condition_summary || 'Always'}
        </span>
      ),
    },
    {
      id: 'template',
      header: 'Sends template',
      cell: (row) => (
        <span className="font-medium text-slate-900">{row.template_name}</span>
      ),
    },
    {
      id: 'send_mode',
      header: 'Delivery',
      cell: (row) => (
        <span className="text-xs text-slate-700">
          {SEND_MODE_LABEL[row.send_mode] ?? row.send_mode}
        </span>
      ),
    },
    {
      id: 'priority',
      header: 'Priority',
      cell: (row) => (
        <span className="tabular-nums text-xs text-slate-600">{row.priority}</span>
      ),
    },
    {
      id: 'is_active',
      header: 'Status',
      cell: (row) => (
        <Badge variant={row.is_active ? 'default' : 'outline'}>
          {row.is_active ? 'Active' : 'Disabled'}
        </Badge>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Email triggers</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Decide which email template gets sent when something happens. Each trigger
            listens for a workflow event, a workflow action, or an API call, and uses
            optional conditions to pick the right template.
          </p>
        </div>
        <Button onClick={() => navigate('/settings/email-triggers/new')}>
          <Plus size={16} />
          New trigger
        </Button>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
          Source:
        </span>
        {KIND_FILTERS.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={kind === f.value ? 'default' : 'outline'}
            onClick={() => setKind(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {error && (
        <p className="text-sm text-rose-600">Couldn't load triggers. Try refreshing.</p>
      )}

      {!isLoading && !error && triggers.length === 0 ? (
        <div className="text-center py-12 px-4 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
          <Zap size={20} className="mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-600">
            No triggers yet - create one to wire an event to an email template.
          </p>
          <Button
            className="mt-3"
            onClick={() => navigate('/settings/email-triggers/new')}
          >
            <Plus size={16} />
            Create the first one
          </Button>
        </div>
      ) : (
        <DataTable<TriggerListRow>
          tableId="email-triggers"
          columns={columns}
          data={triggers}
          isLoading={isLoading}
          rowKey="uuid"
          onRowClick={(row) => navigate(`/settings/email-triggers/${row.uuid}`)}
        />
      )}
    </div>
  )
}

export default EmailTriggersTab
